import React, { useEffect, useRef, useState } from "react";
import { ECLDialogButtonBar, ECLThemeImage } from "../../index";
import "./ECLDialog.css";

/**
 * ECLDialog component renders a dialog with customizable content.
 *
 * @component
 * @example
 * const triggerText = "Open Dialog";
 * const title = "Dialog Title";
 * return (
 *   <ECLDialog triggerText={triggerText} title={title}>
 *     <p>Dialog content goes here.</p>
 *   </ECLDialog>
 * );
 *
 * @param {Object} props - (required) Component props
 * @param {string} props.triggerText - (required) Text for the button that triggers the dialog
 * @param {string} props.title - (required) Title of the dialog
 * @param {function} props.action - (optional) Function to run on confirm button click
 * @param {function} props.cancelAction - (optional) Function to run on cancel button click
 * @param {React.ReactNode} props.children - Content to be displayed inside the dialog
 * @param {bool} props.dropDownModal - (optional) If present will change the dialog to allow elements stack over it
 * @param {string} props.className - (optional) Additional class names for the dialog
 * @param {bool} props.persist - (optional) If true, the dialog will persist after action
 * @param {Array} props.customButtons - (optional) Custom buttons to be displayed in the dialog
 * @param {string} props.triggerClass - (optional) Additional class names for the trigger button
 * @param {string} props.contentSectionClass - (optional) Additional class names for the content section
 * @param {string} props.confirmText - (optional) Text for the confirm button
 * @param {string} props.cancelText - (optional) Text for the cancel button
 * @param {bool} props.mandatory - (optional) If true, the dialog cannot be closed without action
 */
export function clearOpenDialog() {
   const openDialog = document.querySelector("dialog[open]");
   openDialog.close();
}

export default function ECLDialog({
   className = "",
   persist = false,
   customButtons,
   triggerText,
   title,
   children,
   dropDownModal = false,
   triggerClass = "",
   contentSectionClass = "",
   action,
   confirmText,
   cancelText,
   cancelAction,
   mandatory = false,
   inlineStyle,
}) {
   const [open, setOpen] = useState(!triggerText ? true : false);
   const dialogRef = useRef();
   const handleClose = () => {
      dialogRef.current.close();
      if (dropDownModal) {
         document.querySelector("#overlay").style.display = "none";
      }
   };
   const handleOpen = () => {
      if (dropDownModal) {
         dialogRef.current.show();
         document.querySelector("#overlay").style.display = "block";
      } else {
         dialogRef.current.showModal();
      }
   };

   function toggle() {
      if (dialogRef.current.open) {
         handleClose();
         setOpen(false);
      } else {
         handleOpen();
         setOpen(true);
      }
   }

   useEffect(() => {
      // Open modal if no trigger text is provided
      if (!triggerText) {
         dialogRef.current.showModal();
      }
   }, []);
   return (
      <>
         {/* If props.triggerTest isn't defined just leave it open */}
         {triggerText && (
            <button className={`${triggerClass}`} onClick={toggle}>
               {triggerText}
            </button>
         )}

         <dialog
            className={
               dropDownModal
                  ? `${className} ${dropDownModal && "dialog-dropdown"}`
                  : `${className} dialog-dropdown`
            }
            ref={dialogRef}
         >
            <div className="x-out-div">
               <h3 title={title ? title : "SourceOne Prompt"}>
                  {title ? title : "SourceOne Prompt"}
               </h3>
               {/* Closes Modal */}
               {!mandatory && (
                  <div className="cancel-btn-container">
                     <button onClick={toggle}>
                        <ECLThemeImage fontAwesome="xmark" alt="Close Modal" />
                     </button>
                  </div>
               )}
            </div>
            {/* Dynamic Content Passed in */}
            <div
               className={`dialog-content ${contentSectionClass}`}
               style={inlineStyle}
            >
               {open && children}
               {
                  <ECLDialogButtonBar
                     mandatory={mandatory}
                     action={action}
                     confirmText={confirmText}
                     cancelText={cancelText}
                     cancelAction={cancelAction}
                     persist={persist}
                     toggle={toggle}
                     customButtons={customButtons}
                  />
               }
            </div>
         </dialog>
         {dropDownModal && <div id="overlay"></div>}
      </>
   );
}
